import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  CloseIcon,
  SearchIcon,
  CheckIcon,
} from '../components/Dashbaord/chat/components/ui/inputs/ChatIcons';
import fetchApi from '../components/Lib/api';

const SquadFormModal = ({
  show,
  onClose,
  initialSquadData,
  onSaveSquad,
  isLoading,
  error,
}) => {
  // Initialize all state and refs first - before any early returns
  const [squadName, setSquadName] = useState('');
  const [squadDescription, setSquadDescription] = useState('');
  const [currentSelectedMemberIds, setCurrentSelectedMemberIds] = useState(new Set());
  const [selectedMemberFullDetails, setSelectedMemberFullDetails] = useState(new Map());
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [searchUserError, setSearchUserError] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Reset form when modal opens/closes or initialSquadData changes
  useEffect(() => {
    if (show) {
      setSearchUserError(null);
      setMemberSearchTerm('');
      setSearchedUsers([]);

      if (initialSquadData) {
        setSquadName(initialSquadData.name || '');
        setSquadDescription(initialSquadData.description || '');
        const initialIds = new Set(initialSquadData.members?.map(m => m.id) || []);
        setCurrentSelectedMemberIds(initialIds);
        const initialDetailsMap = new Map(initialSquadData.members?.map(m => [m.id, m]) || []);
        setSelectedMemberFullDetails(initialDetailsMap);
      } else {
        setSquadName('');
        setSquadDescription('');
        setCurrentSelectedMemberIds(new Set());
        setSelectedMemberFullDetails(new Map());
      }
    }
  }, [show, initialSquadData]);

  // Function to fetch users based on search term
  const fetchUsers = useCallback(async (searchTerm) => {
    if (!searchTerm) {
      return [];
    }
    setIsSearchingUsers(true);
    setSearchUserError(null);
    try {
      const response = await fetchApi(`/conversations/search_users?search=${encodeURIComponent(searchTerm)}`, 'GET');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to search users.`);
      }
      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching users for search:", err);
      setSearchUserError(err.message || "Failed to load users. Please try again.");
      return [];
    } finally {
      setIsSearchingUsers(false);
    }
  }, []);

  // Effect to handle dynamic user searching with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (memberSearchTerm.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        const users = await fetchUsers(memberSearchTerm);
        setSearchedUsers(users);
      }, 500);
    } else {
      setSearchedUsers([]);
      setIsSearchingUsers(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [memberSearchTerm, fetchUsers]);

  const handleToggleMemberSelection = useCallback((user) => {
    setCurrentSelectedMemberIds((prevIds) => {
      const newSet = new Set(prevIds);
      if (newSet.has(user.id)) {
        newSet.delete(user.id);
      } else {
        newSet.add(user.id);
      }
      return newSet;
    });

    setSelectedMemberFullDetails((prevDetails) => {
      const newMap = new Map(prevDetails);
      if (newMap.has(user.id)) {
        newMap.delete(user.id);
      } else {
        newMap.set(user.id, user);
      }
      return newMap;
    });
  }, []);

  const handleSaveSquad = useCallback(async () => {
    if (!squadName.trim()) {
      alert("Squad name is required.");
      return;
    }
    if (currentSelectedMemberIds.size === 0) {
      alert("At least one member is required for the squad.");
      return;
    }

    const squadData = {
      name: squadName.trim(),
      description: squadDescription.trim(),
      userIds: Array.from(currentSelectedMemberIds),
    };

    try {
      await onSaveSquad(squadData, initialSquadData?.id);
    } catch (err) {
      console.error("Save operation failed:", err);
    }
  }, [
    squadName,
    squadDescription,
    currentSelectedMemberIds,
    onSaveSquad,
    initialSquadData,
  ]);

  // Combine selected members from initial data and new searches for display
  const allSelectedMembers = useMemo(() => {
    const combinedMap = new Map(selectedMemberFullDetails);
    searchedUsers.forEach(user => {
      if (currentSelectedMemberIds.has(user.id) && !combinedMap.has(user.id)) {
        combinedMap.set(user.id, user);
      }
    });
    return Array.from(combinedMap.values());
  }, [currentSelectedMemberIds, selectedMemberFullDetails, searchedUsers]);

  // Early return AFTER all hooks have been called
  if (!show) {
    return null;
  }

  return (
    <div
      role="dialog"
      className="relative z-50"
      style={{zIndex: "1000"}}
      aria-labelledby="modal-title"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
        onClick={onClose}
      ></div>
      <div className="fixed inset-0 z-10 h-full overflow-y-auto">
        <div className="h-full flex items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg pt-6 pb-0 shadow-xl transform transition-all sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 px-5">
              <p
                className="text-black opacity-80 dark:text-white text-lg font-semibold leading-4 tracking-[.01rem]"
                id="modal-title"
                tabIndex="0"
              >
                {initialSquadData ? 'Edit Squad' : 'Create New Squad'}
              </p>
              <button
                tabIndex="0"
                onClick={onClose}
                aria-label="Close modal"
                className="group p-1 rounded-full flex justify-center items-center ease-out text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-x-hidden px-5 pb-4">
              <div className="mb-4">
                <label htmlFor="squad-name" className="mb-2 block text-sm text-black opacity-60 dark:text-white dark:opacity-70 font-semibold">
                  Squad Name
                </label>
                <input
                  id="squad-name"
                  type="text"
                  value={squadName}
                  onChange={(e) => setSquadName(e.target.value)}
                  className="w-full h-10 p-2 rounded-md border text-black bg-gray-50 dark:text-white dark:border-gray-600 dark:bg-gray-700 focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-800 outline-none transition duration-200 ease-out"
                  placeholder="e.g., The Innovators"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="squad-description" className="mb-2 block text-sm text-black opacity-60 dark:text-white dark:opacity-70 font-semibold">
                  Description (Optional)
                </label>
                <textarea
                  id="squad-description"
                  value={squadDescription}
                  onChange={(e) => setSquadDescription(e.target.value)}
                  rows="3"
                  className="w-full p-2 rounded-md border text-black bg-gray-50 dark:text-white dark:border-gray-600 dark:bg-gray-700 focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-800 outline-none transition duration-200 ease-out resize-none"
                  placeholder="Describe your squad's purpose or goals..."
                ></textarea>
              </div>

              <div className="mb-4">
                <label className="mb-2 block text-sm text-black opacity-60 dark:text-white dark:opacity-70 font-semibold">
                  Squad Members
                </label>
                <div className="relative mb-3">
                  <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none">
                    <SearchIcon className="w-5 h-5 stroke-2 text-black opacity-40 dark:text-white dark:opacity-70" />
                  </i>
                  <input
                    type="text"
                    placeholder="Search users to add..."
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    className="w-full h-9 py-2 pl-10 pr-4 border outline-none rounded-md text-sm text-black dark:text-white dark:opacity-70 placeholder:text-black placeholder:opacity-40 dark:placeholder:text-white dark:placeholder:opacity-70 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 duration-200 transition ease-out text-opacity-70 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-800"
                  />
                </div>

                {/* Selected/Current Members Display */}
                {allSelectedMembers.length > 0 && (
                  <div className="mb-4 max-h-[10rem] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent rounded-md border border-gray-200 dark:border-gray-700 p-2">
                    <p className="text-sm font-semibold text-black opacity-70 dark:text-white dark:opacity-80 mb-2">
                      Current Members:
                    </p>
                    {allSelectedMembers.map((user) => (
                      <div
                        key={user.id}
                        className="w-full p-2 flex items-center text-left transition duration-200 ease-out outline-none rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="mr-3 flex-shrink-0">
                          <div
                            className="w-7 h-7 rounded-full bg-cover bg-center bg-gray-300"
                            style={{
                              backgroundImage: `url("${user.avatar || 'https://via.placeholder.com/150'}")`,
                            }}
                          ></div>
                        </div>
                        <div className="w-full flex flex-col items-start overflow-hidden">
                          <p className="text-sm text-black opacity-70 dark:text-white dark:opacity-80 font-semibold truncate">
                            {user.name}
                          </p>
                          <p className="mt-1 text-xs text-black opacity-50 dark:text-white dark:opacity-60 truncate">
                            {user.email}
                          </p>
                        </div>
                        <div className="ml-auto pl-3 flex-shrink-0">
                          <button
                            onClick={() => handleToggleMemberSelection(user)}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs px-2 py-1 rounded border border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                            aria-label={`Remove ${user.name}`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Available Users List for Selection */}
                <div
                  className="scrollbox overflow-y-auto max-h-[15rem] px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent rounded-md border border-gray-200 dark:border-gray-700"
                  tabIndex="0"
                >
                  {isSearchingUsers ? (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Searching users...</p>
                  ) : searchUserError ? (
                    <p className="text-center text-sm text-red-500 dark:text-red-400 py-4">{searchUserError}</p>
                  ) : searchedUsers.length > 0 ? (
                    searchedUsers.map((user) => (
                      <div
                        key={user.id}
                        className="w-full p-3 flex items-center text-left transition duration-200 ease-out outline-none hover:bg-indigo-50 active:bg-indigo-100 focus:bg-indigo-50 dark:hover:bg-gray-700 dark:focus:bg-gray-700 dark:active:bg-gray-600 rounded-md cursor-pointer"
                        onClick={() => handleToggleMemberSelection(user)}
                      >
                        <div className="mr-4 flex-shrink-0">
                          <div
                            className="w-7 h-7 rounded-full bg-cover bg-center bg-gray-300"
                            style={{
                              backgroundImage: `url("${user.avatar || 'https://via.placeholder.com/150'}")`,
                            }}
                          ></div>
                        </div>
                        <div className="w-full flex flex-col items-start overflow-hidden">
                          <p className="text-sm text-black opacity-70 dark:text-white dark:opacity-80 font-semibold leading-4 tracking-[.01rem] truncate">
                            {user.name}
                          </p>
                          <p className="mt-1 text-xs text-black opacity-50 dark:text-white dark:opacity-60 font-normal leading-4 tracking-[.01rem] truncate">
                            {user.email}
                          </p>
                        </div>
                        <div className="ml-auto pl-3 flex items-center flex-shrink-0">
                          <div className="relative flex items-center">
                            <input
                              className="h-5 w-5 appearance-none relative cursor-pointer outline-none rounded border-2 border-gray-400 dark:border-gray-500 transition-all duration-200 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-800"
                              type="checkbox"
                              checked={currentSelectedMemberIds.has(user.id)}
                              onChange={() => handleToggleMemberSelection(user)}
                              aria-labelledby={`user-name-${user.id}`}
                            />
                            <CheckIcon
                              strokeWidth={3}
                              className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white transition-opacity duration-200 pointer-events-none ${
                                currentSelectedMemberIds.has(user.id)
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                          </div>
                        </div>
                        <span
                          id={`user-name-${user.id}`}
                          className="sr-only"
                        >
                          {user.name}
                        </span>
                      </div>
                    ))
                  ) : memberSearchTerm.length > 1 ? (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">No users found for "{memberSearchTerm}".</p>
                  ) : (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Type at least 2 characters to search for users.</p>
                  )}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end items-center px-5 mt-5 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                <button
                  tabIndex="0"
                  onClick={onClose}
                  className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none hover:bg-gray-100 hover:text-gray-700 active:bg-gray-200 dark:hover:bg-gray-700 px-4 text-black opacity-70 dark:text-white dark:opacity-80 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 mr-2"
                >
                  <span className="text-sm font-medium">
                    Cancel
                  </span>
                </button>
                <button
                  tabIndex="0"
                  onClick={handleSaveSquad}
                  className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none dark:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading || !squadName.trim() || currentSelectedMemberIds.size === 0}
                >
                  {isLoading ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <CheckIcon className="w-4 h-4 mr-2" />
                  )}
                  <span className="text-sm font-semibold leading-4 tracking-[.01rem]">
                    {initialSquadData ? 'Save Changes' : 'Create Squad'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SquadFormModal;